"use client";

import Link from "next/link";
import { useState } from "react";

interface FaqItem {
  q: string;
  a: string;
}

interface FaqSection {
  title: string;
  items: FaqItem[];
}

export default function FaqAccordion({
  sections,
  locale,
  ctaLabel,
}: {
  sections: FaqSection[];
  locale: string;
  ctaLabel: string;
}) {
  const [openItem, setOpenItem] = useState<string | null>(null);
  const handleToggle = (key: string) => setOpenItem((p) => (p === key ? null : key));

  return (
    <div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 32 }}>
        {sections.map((section, i) => (
          <a
            key={i}
            href={`#section-${i + 1}`}
            className="bp-mono-sm"
            style={{
              padding: "6px 10px",
              border: "1px solid var(--bp-rule)",
              color: "var(--bp-ink)",
              background: "var(--bp-bg)",
            }}
          >
            {String(i + 1).padStart(2, "0")} · {section.title.toUpperCase()}
          </a>
        ))}
      </div>

      {sections.map((section, si) => (
        <section key={si} id={`section-${si + 1}`} style={{ marginBottom: 56 }}>
          <div className="bp-mono" style={{ marginBottom: 8 }}>
            <span className="bp-bracket">{String(si + 1).padStart(2, "0")} / {section.title.toUpperCase()}</span>
          </div>
          <h2
            style={{
              fontFamily: "var(--bp-serif)",
              fontWeight: 300,
              letterSpacing: "-0.02em",
              fontSize: "clamp(28px, 3vw, 40px)",
              lineHeight: 1.06,
              margin: "12px 0 24px",
              textWrap: "balance" as never,
            }}
          >
            {section.title}
          </h2>
          <div style={{ borderTop: "1px solid var(--bp-rule)" }}>
            {section.items.map((item, ii) => {
              const key = `s${si + 1}-${ii}`;
              const open = openItem === key;
              return (
                <div key={key} style={{ borderBottom: "1px solid var(--bp-rule)" }}>
                  <button
                    onClick={() => handleToggle(key)}
                    aria-expanded={open}
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "22px 0",
                      display: "grid",
                      gridTemplateColumns: "48px 1fr auto",
                      gap: 16,
                      alignItems: "center",
                      background: "none",
                      border: 0,
                      cursor: "pointer",
                    }}
                  >
                    <span className="bp-mono-sm">{String(ii + 1).padStart(2, "0")}</span>
                    <span
                      style={{
                        fontFamily: "var(--bp-serif)",
                        fontSize: 20,
                        fontWeight: 300,
                        letterSpacing: "-0.01em",
                        lineHeight: 1.25,
                      }}
                    >
                      {item.q}
                    </span>
                    <span style={{ fontFamily: "var(--bp-mono)", fontSize: 16 }}>{open ? "−" : "+"}</span>
                  </button>
                  {open && (
                    <div
                      style={{
                        paddingLeft: 64,
                        paddingBottom: 24,
                        paddingRight: 48,
                        fontSize: 15,
                        color: "var(--bp-ink-2)",
                        lineHeight: 1.6,
                      }}
                    >
                      {item.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}

      <div
        style={{
          marginTop: 32,
          padding: "32px",
          background: "var(--bp-ink)",
          color: "var(--bp-bg)",
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 24,
          alignItems: "center",
        }}
        className="bp-faq-cta"
      >
        <div>
          <div className="bp-mono-sm" style={{ color: "oklch(0.72 0 0)", marginBottom: 4 }}>
            STILL HAVE QUESTIONS?
          </div>
          <div
            style={{
              fontFamily: "var(--bp-serif)",
              fontSize: 24,
              fontWeight: 300,
              letterSpacing: "-0.01em",
            }}
          >
            Our team responds within 48 hours.
          </div>
        </div>
        <Link
          href={`/${locale}/contact`}
          className="bp-btn-ghost"
          style={{ borderColor: "var(--bp-bg)", color: "var(--bp-bg)" }}
        >
          {(ctaLabel ?? "CONTACT").toUpperCase()} →
        </Link>
      </div>

      <style>{`
        @media (max-width: 700px) {
          .bp-faq-cta { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
